import React, { useState, useEffect } from 'react';
import { getErrorMessage } from '@utils/commons';
import { useErrors } from '@contexts/ErrorsContext';
import { MentalCapacityAssessmentType } from '../utils';

interface DateCompletedProps {
  mentalCapacityAssessment: MentalCapacityAssessmentType;
  setMentalCapacityAssessment: React.Dispatch<
    React.SetStateAction<MentalCapacityAssessmentType>
  >;
}

const DateCompleted: React.FC<DateCompletedProps> = ({
  mentalCapacityAssessment,
  setMentalCapacityAssessment,
}) => {
  const [dateCompletedErrorMessage, setDateCompletedErrorMessage] = useState<
    string | undefined
  >('');
  const { errors } = useErrors();

  useEffect(() => {
    setDateCompletedErrorMessage(getErrorMessage(errors, 'dateCompleted'));
  }, [errors]);

  const handleDateChange = (field: 'day' | 'month' | 'year', value: string) => {
    setMentalCapacityAssessment(prev => ({
      ...prev,
      dateCompleted: { ...prev.dateCompleted, [field]: value },
    }));
  };

  return (
    <div
      className={`govuk-form-group mt-10 govuk-bp:mt-14 ${dateCompletedErrorMessage ? 'govuk-form-group--error' : ''}`}
    >
      <fieldset
        className="govuk-fieldset"
        role="group"
        aria-describedby="date-completed-date-hint"
      >
        <legend className="govuk-fieldset__legend govuk-fieldset__legend--l">
          <h1 className="govuk-fieldset__heading">Date completed</h1>
        </legend>
        <div id="date-completed-date-hint" className="govuk-hint">
          The date when the mental capacity assessment was made. (For example,
          11, 12 2026)
        </div>
        {dateCompletedErrorMessage && (
          <p id="full-name-input-error" className="govuk-error-message">
            <span className="govuk-visually-hidden">Error:</span>
            {dateCompletedErrorMessage}
          </p>
        )}
        <div className="govuk-date-input__item">
          <div className="govuk-form-group">
            <label
              className="govuk-label govuk-date-input__label"
              htmlFor="recordUpdateDate"
            >
              Day
            </label>

            <input
              className={`govuk-input govuk-date-input__input govuk-input--width-2 ${dateCompletedErrorMessage ? 'govuk-input--error' : ''}`}
              id="dateCompleted"
              name="dateCompleted"
              type="text"
              inputMode="numeric"
              maxLength={2}
              placeholder="DD"
              value={mentalCapacityAssessment.dateCompleted.day}
              onChange={e => {
                const { value } = e.target;
                if (/^[0-9]*$/.test(value) && value.length <= 2) {
                  handleDateChange('day', value);
                }
              }}
            />
          </div>
        </div>
        <div className="govuk-date-input__item">
          <div className="govuk-form-group">
            <label
              className="govuk-label govuk-date-input__label"
              htmlFor="date-completed-date-month"
            >
              Month
            </label>
            <input
              className={`govuk-input govuk-date-input__input govuk-input--width-2 ${dateCompletedErrorMessage ? 'govuk-input--error' : ''}`}
              id="date-completed-date-month"
              name="date-completed-date-month"
              type="text"
              inputMode="numeric"
              placeholder="MM"
              maxLength={2}
              value={mentalCapacityAssessment.dateCompleted.month}
              onChange={e => {
                const { value } = e.target;
                if (/^[0-9]*$/.test(value) && value.length <= 2) {
                  handleDateChange('month', value);
                }
              }}
            />
          </div>
        </div>
        <div className="govuk-date-input__item">
          <div className="govuk-form-group">
            <label
              className="govuk-label govuk-date-input__label"
              htmlFor="date-completed-date-year"
            >
              Year
            </label>
            <input
              className={`govuk-input govuk-date-input__input govuk-input--width-4 ${dateCompletedErrorMessage ? 'govuk-input--error' : ''}`}
              id="date-completed-date-year"
              name="date-completed-date-year"
              type="text"
              inputMode="numeric"
              placeholder="YYYY"
              maxLength={4}
              value={mentalCapacityAssessment.dateCompleted.year}
              onChange={e => {
                const { value } = e.target;
                if (/^[0-9]*$/.test(value) && value.length <= 4) {
                  handleDateChange('year', value);
                }
              }}
            />
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default DateCompleted;
